/** @jsxImportSource @emotion/react */

import { COLORS } from '@btime/constants'
import { css } from '@emotion/react'
import { useAutoFocus } from '@btime/react-hooks'
import React from 'react'
import TextareaAutosize from 'react-textarea-autosize'

import { SmallText } from '../presentation/Text'
import { inputStyle } from './InputWrapper'
import { useBasicInputControl } from './Input'
import Icon from '../shapes/Icon'
import Markdown from '../presentation/Markdown'

const Textarea = ({
  value,
  autoFocus,
  onStopTyping,
  onChange,
  limit,
  replaceValue,
  useMarkdown,
  rows,
  disabled,
  disableRemount,
  ...props
}) => {
  const inputRef = React.useRef(null)
  useAutoFocus(inputRef, autoFocus)

  const [showResult, setShowResult] = React.useState(false)
  const toggleResult = React.useCallback(() => setShowResult(!showResult), [showResult])

  const style = css`
    ${inputStyle}
    position: relative;

    & .markdownToggler,
    & .markdownMessage {
      position: absolute;
      right: 1rem;
      z-index: 10;
    }

    & .markdownToggler {
      display: block;
      top: 0.7rem;
    }

    & .markdownMessage {
      bottom: 0.2rem;
      text-align: right;
      background: white;
      padding: 0.1rem;
    }

    & .markdown {
      min-height: 10rem;
      padding: 1rem 1rem 2rem 1rem;
      border-radius: 5px;
      border: dashed 1px ${COLORS.light3};
    }

    & .input {
      resize: vertical;
      display: ${showResult ? 'none' : 'block'};
      ${useMarkdown ? 'padding-bottom: 2rem;' : ''}
    }
    ${disabled
      ? `
			opacity: 0.5;
			background-color: ${COLORS.light2};
		`
      : ''}
  `

  const [localValue, { handleChange, handleBlur }] = useBasicInputControl({
    value,
    onStopTyping,
    onChange,
    limit,
    replaceValue,
    disableRemount
  })

  return (
    <div css={style} className="textarea">
      {useMarkdown && !disabled && (
        <div>
          <a className="markdownToggler" onClick={toggleResult}>
            <Icon name={showResult ? 'BsPencil' : 'BsEye'} size="1rem" color={COLORS.dark3} />
          </a>

          <div className="markdownMessage">
            <SmallText size="xsmall" color={COLORS.dark3}>
              Este componente aceita markdown{' '}
              <a
                target="_blank"
                href="https://github.com/adam-p/markdown-here/wiki/Markdown-Cheatsheet"
                rel="noopener noreferrer"
              >
                Mais informações
              </a>
            </SmallText>
          </div>
        </div>
      )}

      {showResult && <Markdown value={localValue} color={COLORS.dark3} />}
      <TextareaAutosize
        autoComplete="none"
        className="input"
        ref={inputRef}
        onChange={handleChange}
        onBlur={handleBlur}
        value={localValue || ''}
        disabled={disabled}
        minRows={rows || (useMarkdown ? 5 : 1)}
        {...props}
      />
    </div>
  )
}

export default Textarea
